import type { Routine } from "instantdb/types";
import { extractDateFromISOString, extractDateFromDate } from "@/utilities/dateFormatter";

export const isRoutineActiveToday = (routine: Routine): boolean => {
  if (routine.dailyRoutine) return true;
  if (!routine.selectedDays?.length) return false;

  const today = new Date();
  const dayName = today.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase() as
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  return routine.selectedDays.some((day) => day.day === dayName);
};

// Check if a routine is active for a specific date
export const isRoutineActiveForDate = (routine: Routine, date: string): boolean => {
  if (routine.dailyRoutine) return true;
  if (!routine.selectedDays?.length) return false;

  const dayOfWeek = new Date(date).toLocaleDateString("en-US", { weekday: "long" }).toLowerCase() as
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";

  return routine.selectedDays.some((day) => {
    if (typeof day === "string") return day === dayOfWeek;
    return day?.day === dayOfWeek;
  });
};

export const hasDayCompleted = (routine: Routine, activeDate?: string): boolean => {
  const today = extractDateFromDate(new Date());
  const dateToCheck = activeDate ?? today;

  // Access daysCompleted through the linked relationship with proper typing
  const daysCompleted = routine.daysCompleted ?? [];
  const dayCompletedEntry = daysCompleted.find(
    (dayCompleted) => extractDateFromISOString(dayCompleted.date) === dateToCheck,
  );

  if (!dayCompletedEntry) return false;

  // For routines with no steps, check if it was explicitly marked as completed
  if (routine.steps?.length === 0) {
    return dayCompletedEntry.completed === true;
  }

  // For routines with steps, check if all steps are completed
  const routineStepIds = (routine.steps ?? [])
    .map((step) => step.id)
    .filter((id): id is string => id != null);
  return dayCompletedEntry.stepsCompleted?.length === routineStepIds?.length;
};

export const hasUpcomingRoutines = (routines: Routine[]) =>
  routines.some((routine) => !isRoutineActiveToday(routine));

export const hasCompletedRoutines = (routines: Routine[]) =>
  routines.some((routine) => hasDayCompleted(routine as Routine));

export const getRoutineCompletionPercentage = (routine: Routine, date?: string): number => {
  if (routine.steps?.length === 0) {
    const today = date ?? extractDateFromDate(new Date());
    const dayCompletedEntry = (routine.daysCompleted ?? []).find(
      (dayCompleted) => dayCompleted.date.split("T")[0] === today,
    );
    return dayCompletedEntry?.completed ? 100 : 0;
  }

  const routineStepIds = (routine.steps ?? [])
    .map((step) => step.id)
    .filter((id): id is string => id != null);
  const today = date ?? extractDateFromDate(new Date());
  const dayCompletedEntry = (routine.daysCompleted ?? []).find(
    (dayCompleted) => dayCompleted.date.split("T")[0] === today,
  );
  const completedSteps = dayCompletedEntry?.stepsCompleted?.length ?? 0;
  const totalSteps = routineStepIds.length;
  return totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
};

export const sortDays = (days: { day: string; datetime: string }[]) => {
  const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  return [...(days ?? [])].sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
};

interface DayCompletedEntry {
  date: string;
  stepsCompleted?: string[];
  completed?: boolean;
}

export const mapRoutineStepIdsToCompletion = (
  routineStepIds: string[],
  dayCompletedEntry: DayCompletedEntry | undefined,
) => {
  const result: Record<string, boolean> = {};

  for (const routineStepId of routineStepIds) {
    result[routineStepId] = (dayCompletedEntry?.stepsCompleted ?? []).includes(routineStepId);
  }

  return result;
};

export const getBrightRoutineColor = (routine: Routine) => {
  if (!routine.color) return undefined;
  const [hue, _, __] = routine.color.split(" ");
  return `hsl(${hue} 100% 35%)`;
};

// Check if all routines for a specific day are completed
export const areAllRoutinesCompletedForDay = (routines: Routine[], date: string): boolean => {
  // Get routines that are active for the given day
  const activeRoutines = routines.filter((routine) => {
    // For daily routines, they're always active
    if (routine.dailyRoutine) return true;

    // For scheduled routines, check if the day matches
    if (!routine.selectedDays?.length) return false;

    const dayOfWeek = new Date(date)
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase() as
      | "monday"
      | "tuesday"
      | "wednesday"
      | "thursday"
      | "friday"
      | "saturday"
      | "sunday";

    return routine.selectedDays.some((day) => {
      if (typeof day === "string") return day === dayOfWeek;
      return day?.day === dayOfWeek;
    });
  });

  // If no routines are active for this day, return false (day is not "completed")
  if (activeRoutines.length === 0) return false;

  // Check if all active routines are completed
  return activeRoutines.every((routine) => hasDayCompleted(routine as Routine, date));
};
